import * as React from "react";
import { isEmpty } from "lodash";
import {
  getLocalStorage,
  keyExists,
  userIsAuthenticated
} from "../../generic/Helpers";
import { IAuthAction, IAuthState } from "../../types/Auth";
import { IAppState } from "../../store/Store";
import LoginContainer from "../../containers/auth/LoginContainer";
import { connect } from "react-redux";
import { AsyncThunkAction } from "../../types/Requests";
import { bindActionCreators } from "redux";
import {
  checkIfTokenIsValid,
  logoutUser,
  getTranslations
} from "../../actions/AuthActions";
import { getCustomer } from "../../actions/CustomerActions";
import { TGetTranslation } from "../../types/Translation";
import { matchPath, RouteComponentProps, withRouter } from "react-router";
import { TCustomerState, TGetCustomerAction } from "../../types/Customer";
import { config } from "../../generic/Config";

interface IStateProps {
  authState: IAuthState;
}

interface IDispatchProps {
  logoutUser: () => Promise<any>;
  checkIfTokenIsValid: () => AsyncThunkAction<IAuthState, IAuthAction>;
  getCustomer: (
    customerId: number
  ) => AsyncThunkAction<TCustomerState, TGetCustomerAction>;
  getTranslations: (
    langCode: string
  ) => AsyncThunkAction<IAppState, TGetTranslation>;
}

type IProps = IStateProps & IDispatchProps & RouteComponentProps<any>;

interface IState {
  isValidatingToken: boolean;
  fetchedCustomerId: number | null;
}

export const authenticated = (Component: React.ComponentType) => {
  class requiresAuthentication extends React.Component<IProps, IState> {
    constructor(props: IProps) {
      super(props);
      this.state = {
        isValidatingToken: false,
        fetchedCustomerId: null
      };
    }

    private getCustomerIdFromPath(): number | null {
      const match: any = matchPath(this.props.history.location.pathname, {
        path: "/app/customer/:customerId",
        exact: false,
        strict: false
      });
      return match && match.params.customerId
        ? parseInt(match.params.customerId)
        : null;
    }

    private handleAuthCheck(): void {
      if (
        keyExists("token") &&
        isEmpty(this.props.authState.data) &&
        !this.state.isValidatingToken
      ) {
        this.setState({ isValidatingToken: true });
        this.props.checkIfTokenIsValid();
        this.props.getTranslations(
          keyExists("lang") ? getLocalStorage("lang") : config.DEFAULT_LANG
        );
      }
    }

    private handleCustomerFetch(): void {
      const customerId = this.getCustomerIdFromPath();
      if (
        customerId &&
        this.props.authState.data &&
        this.state.fetchedCustomerId !== customerId
      ) {
        this.setState({ fetchedCustomerId: customerId });
        this.props.getCustomer(customerId);
      }
    }

    public componentDidMount(): void {
      this.handleAuthCheck();
      this.handleCustomerFetch();
    }

    public componentDidUpdate(prevProps: IProps): void {
      if (prevProps.authState.data !== this.props.authState.data) {
        if (this.props.authState.data) {
          this.setState({ isValidatingToken: false });
        }
        this.handleCustomerFetch();
      }

      if (prevProps.history.location.pathname !== this.props.history.location.pathname) {
        this.handleCustomerFetch();
      }
    }

    public render() {
      return userIsAuthenticated(this.props.authState) ? (
        <Component />
      ) : (
        <LoginContainer />
      );
    }
  }

  const mapStateToProps = (state: IAppState): IStateProps => {
    return {
      authState: state.authState
    };
  };

  const mapDispatchToProps = (dispatch: any): IDispatchProps => {
    return bindActionCreators(
      { checkIfTokenIsValid, logoutUser, getTranslations, getCustomer },
      dispatch
    );
  };

  return withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(requiresAuthentication)
  );
};
